import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes, STUDENT } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField, FieldRadioButton, NamedLink, FieldCheckbox } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';
import { parse } from '../../../util/urlHelpers';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => {
  const location = useLocation();
  const search = parse(location?.search || '');
  const { ref } = search || {};

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{
        userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
        referralCode: ref || '',
        isFamilyChildSignUp: props.preselectedIsChild || false,
      }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          referralError,
          values,
          form: formApi
        } = formRenderProps;

        const { userType } = values || {};

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );

        // confirm password
        const confirmPasswordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.confirmPasswordRequired',
        });
        const confirmPasswordMustMatchMessage = intl.formatMessage({
          id: 'SignupForm.confirmPasswordMustMatch',
        });
        const confirmPasswordRequired = validators.requiredStringNoTrim(confirmPasswordRequiredMessage);
        const confirmPasswordMatch = value =>
          value === values.password ? undefined : confirmPasswordMustMatchMessage;
        const confirmPasswordValidators = validators.composeValidators(
          confirmPasswordRequired,
          confirmPasswordMatch
        );
        const disallowedRe = /[^\p{L}\p{M}\s]/u;
        const preventDisallowedBeforeInput = e => {
          if (e && typeof e.data === 'string' && disallowedRe.test(e.data)) {
            e.preventDefault();
          }
        };
        const preventDisallowedKeyDown = e => {
          if (!e) return;
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          const navKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'];
          if (navKeys.includes(e.key)) return;
          if (typeof e.key === 'string' && e.key.length === 1 && disallowedRe.test(e.key)) {
            e.preventDefault();
          }
        };
        const preventDisallowedPaste = e => {
          const t = e && e.clipboardData ? e.clipboardData.getData('text') : '';
          if (t && disallowedRe.test(t)) {
            e.preventDefault();
          }
        };
        // Custom user fields. Since user types are not supported here,
        // only fields with no user type id limitation are selected.
        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const consentRequired = values?.isFamilyChildSignUp && !values?.pdplGdprConsent;
        const submitDisabled = invalid || submitInProgress || !values?.userType || consentRequired;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.formTitle}>
              <div className={css.formHeading}>
                <FormattedMessage id="SignupForm.createAccount" />
              </div>
              <div className={css.formSubHeading}>
                <FormattedMessage id="SignupForm.subDetail" />
              </div>
            </div>
            <div className={css.userTypeWrapper}>
              <FieldRadioButton
                id="student"
                name="userType"
                label={intl.formatMessage({
                  id: 'SignupForm.signStudents',
                })}
                value="student"
              />
              <FieldRadioButton
                id="parent"
                name="userType"
                label={intl.formatMessage({
                  id: 'SignupForm.signParent',
                  defaultMessage: 'Sign up as Parent',
                })}
                value="parent"
              />
              <FieldRadioButton
                id="tutor"
                name="userType"
                label={intl.formatMessage({
                  id: 'SignupForm.signTeachers',
                })}
                value="tutor"
              />
            </div>
            {/* <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          /> */}

            {showDefaultUserFields || true ? (
              <div className={css.defaultUserFields}>
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.firstNameRequired',
                      })
                    )}
                    onBeforeInput={preventDisallowedBeforeInput}
                    onKeyDown={preventDisallowedKeyDown}
                    onPaste={preventDisallowedPaste}
                  />
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                    onBeforeInput={preventDisallowedBeforeInput}
                    onKeyDown={preventDisallowedKeyDown}
                    onPaste={preventDisallowedPaste}
                  />
                </div>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />

                <UserFieldDisplayName
                  formName="SignupForm"
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />

                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({
                    id: 'SignupForm.passwordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.passwordPlaceholder',
                  })}
                  validate={passwordValidators}
                  showPasswordToggle
                />
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                  name="confirmPassword"
                  autoComplete="new-password"
                  label={intl.formatMessage({
                    id: 'SignupForm.confirmpasswordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.confirmPasswordPlaceholder',
                  })}
                  validate={confirmPasswordValidators}
                  showPasswordToggle
                />

                <UserFieldPhoneNumber
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />
                {userType == STUDENT ? (
                  <div className={css.referralCode}>
                    <FieldTextInput
                      className={css.referralCodeInput}
                      type="text"
                      id={formId ? `${formId}.referralCode` : 'referralCode'}
                      name="referralCode"
                      label={intl.formatMessage({
                        id: 'SignupForm.referralCodeLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.referralCodePlaceholder',
                      })}
                    />
                    {referralError && (
                      <div className={css.referralError}>{referralError}</div>
                    )}

                    <div className={css.checkboxField}>
                      <FieldCheckbox
                        id="isFamilyChildSignUp"
                        name="isFamilyChildSignUp"
                        label={intl.formatMessage({
                          id: 'SignupForm.isFamilyChildLabel',
                        })}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (!checked) {
                            formApi.change('parentEmail', undefined);
                            formApi.change('pdplGdprConsent', undefined);
                          }
                        }}
                      />
                    </div>

                    {values?.isFamilyChildSignUp && (
                      <div className={css.childSignUpFields}>
                        <FieldTextInput
                          type="email"
                          id="parentEmail"
                          name="parentEmail"
                          label={intl.formatMessage({
                            id: 'SignupForm.parentEmailLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'SignupForm.parentEmailPlaceholder',
                          })}
                          validate={validators.composeValidators(
                            validators.required(
                              intl.formatMessage({
                                id: 'SignupForm.parentEmailRequired',
                              })
                            ),
                            validators.emailFormatValid(
                              intl.formatMessage({
                                id: 'SignupForm.parentEmailInvalid',
                              })
                            )
                          )}
                        />

                        <div className={css.checkboxField}>
                          <FieldCheckbox
                            id="pdplGdprConsent"
                            name="pdplGdprConsent"
                            label={intl.formatMessage({
                              id: 'SignupForm.pdplGdprConsentLabel',
                            })}
                            validate={validators.required(
                              intl.formatMessage({
                                id: 'SignupForm.pdplGdprConsentRequired',
                              })
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="SignupForm.signUp" />
                <IconCard type="rightarrow" />
              </PrimaryButton>
              <div className={css.loginLink}>
                <FormattedMessage id="SignupForm.alreadyHaveAccount" />
                <NamedLink name="LoginPage">
                  <FormattedMessage id="SignupForm.logIn" />
                </NamedLink>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupFormInner = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupFormInner;
